'use client'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { TransactionTitle } from '~/components/component-library/TransactionTitle'
import { ApproveTransactionDialogContent } from '~/components/smart-accounts/ApproveTransactionDialogContent'
import { QUERY_KEYS } from '~/constants/react-query'
import { useAuth } from '~/context/AuthProvider'
import { useHandleMerkleTransactionError } from '~/hooks/merkle-transactions/useHandleMerkleTransactionError'
import { useRequiredActiveOrg } from '~/hooks/org'
import { useNavigation } from '~/hooks/useNavigation'
import { getPostOnboardingChecklistQueryKey } from '~/hooks/usePostOnboardingChecklist'
import {
  getSplitsErrorCodeString,
  useServerRequests,
} from '~/hooks/useRequests'
import { OrgTransaction } from '~/types'

type WorkspaceAutoEarn = {
  enabledChainIds: number[]
  eligibleChainIds: number[]
  pendingTransactionId: string | null
}

// Workspace auto-earn state, and `toggle` to create the Treasury transaction
// that turns it on or off and open it for signing.
export const useWorkspaceAutoEarn = () => {
  const { id: orgId } = useRequiredActiveOrg()
  const { serverGetRequest, serverPostRequest } = useServerRequests()
  const { setGlobalModalState, closeGlobalModal } = useAuth()
  const queryClient = useQueryClient()
  const navigate = useNavigation()
  const handleMerkleTransactionError = useHandleMerkleTransactionError()

  const queryKey = [QUERY_KEYS.WORKSPACE_AUTO_EARN, orgId]
  const { data } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await serverGetRequest<WorkspaceAutoEarn>({
        path: '/api/org/workspace_auto_earn',
        params: { orgId },
      })
      if (!response) throw new Error('Failed to fetch workspace auto-earn')
      return response.data
    },
  })

  const { mutate: toggle, isPending } = useMutation({
    mutationFn: async (enabled: boolean) => {
      const response = await serverPostRequest<{
        transaction: OrgTransaction
      }>({
        path: '/api/org/workspace_auto_earn',
        params: { orgId, enabled },
        processingMessage: 'Creating transaction...',
        errorMessage: 'Failed to create auto-earn transaction',
        overrideErrorMessageOptions: handleMerkleTransactionError,
      })
      return response?.data.transaction
    },
    onSuccess: (transaction) => {
      if (!transaction) return

      // Set here, not by refetch: a failed refetch would re-enable the toggle
      queryClient.setQueryData<WorkspaceAutoEarn>(
        queryKey,
        (prev) => prev && { ...prev, pendingTransactionId: transaction.id },
      )
      queryClient.setQueryData(
        [QUERY_KEYS.TRANSACTION, orgId, transaction.id],
        transaction,
      )
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TRANSACTIONS] })
      toast.success('Transaction created', {
        id: transaction.id,
        description: <TransactionTitle transaction={transaction} unstyled />,
      })
      setGlobalModalState({
        isOpen: true,
        content: (
          <ApproveTransactionDialogContent
            transaction={transaction}
            globalModal
            onClose={closeGlobalModal}
            onConfirmation={() => {
              queryClient.invalidateQueries({ queryKey })
              queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.ORG_SMART_ACCOUNTS, orgId],
              })
              queryClient.invalidateQueries({
                queryKey: getPostOnboardingChecklistQueryKey(orgId),
              })
            }}
            onSubmitError={(error) => {
              if (
                getSplitsErrorCodeString(error) ===
                'MERKLE_TRANSACTION_SUBMISSION_FAILED'
              ) {
                queryClient.invalidateQueries({
                  queryKey: [QUERY_KEYS.TRANSACTIONS],
                })
                navigate(`/accounts/${transaction.smartAccountAddress}/`)
              }
            }}
          />
        ),
      })
    },
  })

  return { data, toggle, isPending }
}
